import * as React from 'react'

import {
  Building2Icon,
  CopyIcon,
  MapPinIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'
import { observer } from 'mobx-react'

import { IWorkflowStep, PermitFieldType } from '~/client/graph'
import { AvatarSize } from '~/client/src/shared/components/Avatar/Avatar'
import { Button } from '~/client/src/shared/components/Button/Button'
import CompactCompaniesDirectory from '~/client/src/shared/components/CompactCompaniesDirectory/CompactCompaniesDirectory'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import LocationEquipmentResourcePicker from '~/client/src/shared/components/LocationPicker/LocationEquipmentResourcePicker'
import { PERMIT_FIELD_GLYPHS } from '~/client/src/shared/components/PermitFieldIcon/permitFieldGlyphs'
import {
  Dialog,
  DialogContent,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import {
  Field,
  FieldLabel,
} from '~/client/src/shared/components/shadcn-ui/field'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import UserFieldPickerDialog from '~/client/src/shared/components/UserFieldPickerDialog'
import { UserFieldPickerTab } from '~/client/src/shared/components/UserFieldPickerDialog/UserFieldPickerDialog'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { userPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  AndOrOperator,
  IsNotOperator,
} from '~/client/src/shared/models/LogicOperation'
import OperationRule from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import { getWorkflowFieldTypeLabel } from '~/client/src/shared/utils/WorkflowFieldTypeHelpers'

import BallInCourtRulesTableRowStore from './BallInCourtRulesTableRow.store'
import CategorySelectorRow from './components/CategorySelectorRow'
import RuleNameInput from './components/RuleNameInput'

interface IAssignedField {
  id: string
  type: PermitFieldType
  caption: string
  stepNumber: number
  fieldIndex: number
}

// localization: translated

interface IProps {
  bicRule: OperationRule
  ruleIndex: number
  allWorkflowSteps?: IWorkflowStep[]

  tagsStore: TagsStore
  companiesStore: CompaniesStore

  getUsersFromAssignment: (assignmentExpressions: string[]) => User[]
  onRemoveRuleClick: (ruleId: string) => void
  onDuplicateRuleClick: (ruleId: string) => void
  onChangeUserForRule: (
    rule: OperationRule,
    userId: string,
    isNew?: boolean,
  ) => void
  onRuleExpressionChange: (
    rule: OperationRule,
    locationAndCompanyExpression: string,
  ) => void
  onFieldToggleForRule?: (
    rule: OperationRule,
    fieldId: string,
    isSelected: boolean,
  ) => void
  onFieldsToggleForRule?: (
    rule: OperationRule,
    fieldIds: string[],
    isSelected: boolean,
  ) => void
  onDiffCommitForRule?: (
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => void
  onRenameRule: (rule: OperationRule, name: string) => void
  /** Set when companies can be assignees. Removes a company chip; picker saves go to onDiffCommitForRule. */
  onCompanyDiffCommitForRule?: (
    rule: OperationRule,
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => void
}

const TAGS_DIALOG_CLASSES =
  'tw:flex tw:h-[700px] tw:w-[400px] tw:flex-col tw:gap-0 tw:overflow-hidden tw:p-0 tw:sm:max-w-[400px] tw:[&>*]:min-h-0 tw:[&>*]:flex-1'
// Lines the card's sections up with the rule name, past the number badge.
const SECTION_CLASSES = 'tw:flex tw:flex-col tw:gap-2 tw:pl-11'

@observer
export default class BallInCourtRulesTableRow extends React.Component<IProps> {
  private readonly store: BallInCourtRulesTableRowStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = new BallInCourtRulesTableRowStore(
      props.tagsStore,
      props.bicRule,
      props.onRuleExpressionChange,
    )
  }

  public componentDidUpdate(prevProps: IProps) {
    const { bicRule } = this.props
    if (bicRule !== prevProps.bicRule) {
      this.store.init(bicRule)
    }
  }

  public render() {
    const { selectedBicRule } = this.store

    return (
      <section
        className="tw:flex tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:p-4"
        aria-label={this.fallbackName}
      >
        {this.renderHeader()}
        {this.renderAssignees(selectedBicRule)}
        <div className={SECTION_CLASSES}>
          <span className="tw:text-app-meta tw:text-muted-foreground">
            {Localization.translator.assignAsBicWhenWorkflowMatches}:
          </span>
          {this.renderRuleExpression()}
        </div>
      </section>
    )
  }

  private renderHeader(): JSX.Element {
    const { ruleIndex, bicRule, onDuplicateRuleClick, onRemoveRuleClick } =
      this.props
    const nameInputId = `bic-rule-name-${bicRule.id}`

    return (
      <div className="tw:flex tw:items-end tw:gap-3">
        <span className="tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-muted tw:text-app-dense-medium tw:text-foreground">
          {ruleIndex}
        </span>
        <Field className="tw:min-w-0 tw:flex-1 tw:gap-1.5">
          <FieldLabel htmlFor={nameInputId}>
            {Localization.translator.ruleName}
            <span className="tw:font-normal tw:text-muted-foreground">
              {Localization.translator.optionalInParentheses}
            </span>
          </FieldLabel>
          <RuleNameInput
            id={nameInputId}
            name={bicRule.name}
            placeholder={this.fallbackName}
            onCommit={this.handleRenameRule}
          />
        </Field>
        <div className="tw:flex tw:shrink-0 tw:gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.duplicate}
            onClick={onDuplicateRuleClick.bind(null, bicRule.id)}
          >
            <CopyIcon className="tw:-scale-x-100" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.deleteRule}
            onClick={onRemoveRuleClick.bind(null, bicRule.id)}
          >
            <Trash2Icon />
          </Button>
        </div>
      </div>
    )
  }

  private renderAssignees(operationRule: OperationRule): JSX.Element {
    const {
      getUsersFromAssignment,
      companiesStore,
      onCompanyDiffCommitForRule,
    } = this.props
    const { showUsersDirectory } = this.store

    const users = getUsersFromAssignment(
      operationRule.assignmentsAsSubexpressions,
    )
    const assignedFields = this.getAssignedFields(operationRule)
    const { assignedCompanyIds } = operationRule
    const hasAssignees =
      !!users?.length || !!assignedFields.length || !!assignedCompanyIds.length

    return (
      <div className={SECTION_CLASSES}>
        <span className="tw:text-label-md-caps-prominent tw:uppercase tw:text-muted-foreground">
          {Localization.translator.assignees}
        </span>
        {hasAssignees && (
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            {users?.map(user => (
              <Tag
                key={user.id}
                avatar={
                  <UserProfilePreview
                    user={user}
                    isCompact
                    avatarSize={AvatarSize.XMinuscule}
                  />
                }
                onDismiss={this.handleRemoveUserFromRule.bind(
                  null,
                  operationRule,
                  user.id,
                )}
              >
                {user.fullName}
              </Tag>
            ))}
            {assignedFields.map(field => {
              const FieldIcon = PERMIT_FIELD_GLYPHS[field.type]
              return (
                <Tag
                  key={field.id}
                  icon={<FieldIcon className="tw:size-4" />}
                  onDismiss={this.handleRemoveFieldFromRule.bind(
                    null,
                    operationRule,
                    field.id,
                  )}
                >
                  {`${field.stepNumber}.${field.fieldIndex} ${field.caption}`}
                </Tag>
              )
            })}
            {assignedCompanyIds.map(companyId => (
              <Tag
                key={companyId}
                icon={<Building2Icon className="tw:size-4" />}
                onDismiss={this.handleRemoveCompanyFromRule.bind(
                  null,
                  operationRule,
                  companyId,
                )}
              >
                {companiesStore.getCompanyNameById(
                  companyId,
                  Localization.translator.unknownDeletedObject,
                )}
              </Tag>
            ))}
          </div>
        )}
        <div className="tw:flex tw:gap-1">
          <Button
            variant="primary-ghost"
            size="sm"
            onClick={showUsersDirectory.bind(null, UserFieldPickerTab.BY_USERS)}
          >
            <PlusIcon data-icon="inline-start" />
            {Localization.translator.addUser}
          </Button>
          {onCompanyDiffCommitForRule && (
            <Button
              variant="primary-ghost"
              size="sm"
              onClick={showUsersDirectory.bind(
                null,
                UserFieldPickerTab.BY_COMPANY,
              )}
            >
              <PlusIcon data-icon="inline-start" />
              {Localization.translator.addCompany}
            </Button>
          )}
        </div>
        {this.renderUserPickerDialog(users, operationRule)}
      </div>
    )
  }

  private renderRuleExpression(): JSX.Element {
    const {
      locationElements,
      companyElements,
      locationNotOperatorValue,
      companyNotOperatorValue,
      toggleLocationPicker,
      toggleCompaniesPicker,
      removeTagFromElements,
    } = this.store

    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        {this.renderLocationPickerDialog()}
        {this.renderCompanyPickerDialog()}

        <CategorySelectorRow
          categoryNameAndIconElement={this.locationCategorySelector}
          categoryElements={locationElements}
          noElementsTitle={Localization.translator.any}
          isNotOperatorValue={locationNotOperatorValue}
          onAddNewElement={toggleLocationPicker}
          onTagRemove={removeTagFromElements}
          onNotOperatorChange={this.handleNotLocationOperatorChange}
        />
        <CategorySelectorRow
          categoryNameAndIconElement={this.companyCategorySelector}
          categoryElements={companyElements}
          noElementsTitle={Localization.translator.any}
          categoryOperator={AndOrOperator.AND}
          isNotOperatorValue={companyNotOperatorValue}
          onAddNewElement={toggleCompaniesPicker}
          onTagRemove={removeTagFromElements}
          onNotOperatorChange={this.handleNotCompanyOperatorChange}
        />
      </div>
    )
  }

  private renderUserPickerDialog(
    selectedUsers: User[],
    operationRule: OperationRule,
  ): JSX.Element {
    const { allWorkflowSteps, onCompanyDiffCommitForRule } = this.props
    const { shouldShowUsersDirectory, usersDirectoryTab, hideUsersDirectory } =
      this.store

    return (
      <UserFieldPickerDialog
        isOpen={shouldShowUsersDirectory}
        initialTab={usersDirectoryTab}
        title={Localization.translator.bicUsers}
        selectedUsers={selectedUsers}
        onUserAdd={this.handleAddUserToRule.bind(null, operationRule)}
        onUserRemove={this.handleRemoveUserFromRule.bind(null, operationRule)}
        onDiffCommit={
          this.props.onDiffCommitForRule
            ? this.handleDiffCommitForRule.bind(null, operationRule)
            : undefined
        }
        allWorkflowSteps={allWorkflowSteps}
        selectedFieldIds={operationRule.assignedFieldIds}
        onFieldToggle={this.handleFieldToggle}
        onFieldsToggle={this.handleFieldsToggle}
        selectedCompanyIds={operationRule.assignedCompanyIds}
        canPickCompanies={!!onCompanyDiffCommitForRule}
        onClose={hideUsersDirectory}
      />
    )
  }

  private renderLocationPickerDialog(): JSX.Element {
    const {
      shouldShowLocationPicker,
      selectedLocationTagIds,
      hideLocationPicker,
      setLocationTags,
    } = this.store

    return (
      <Dialog
        open={shouldShowLocationPicker}
        onOpenChange={open => !open && hideLocationPicker()}
      >
        <DialogContent showCloseButton={false} className={TAGS_DIALOG_CLASSES}>
          <div className="tw:flex tw:flex-col">
            <ListSelector.Title
              title={Localization.translator.selectResources}
              onClose={hideLocationPicker}
            />
            <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
              <LocationEquipmentResourcePicker
                mode="multi"
                allowEmptySelection={true}
                selectedIds={selectedLocationTagIds}
                onSelect={setLocationTags}
                onClose={hideLocationPicker}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  private renderCompanyPickerDialog(): JSX.Element {
    const {
      shouldShowCompaniesPicker,
      selectedCompanyTagIds,
      hideCompaniesPicker,
      addCompanyTag,
      clearCompanyTags,
      removeTagFromElements,
    } = this.store

    return (
      <Dialog open={shouldShowCompaniesPicker}>
        <DialogContent showCloseButton={false} className={TAGS_DIALOG_CLASSES}>
          <CompactCompaniesDirectory
            className="tw:relative"
            isPhoneHidden={true}
            isSelectionMode={true}
            selectedCompanyIds={selectedCompanyTagIds}
            onHeaderCloseClick={hideCompaniesPicker}
            onCompanyAddClick={addCompanyTag}
            onCompanyRemoveClick={removeTagFromElements}
            onResetClick={clearCompanyTags}
          />
        </DialogContent>
      </Dialog>
    )
  }

  private get fallbackName(): string {
    return `${Localization.translator.rule} ${this.props.ruleIndex}`
  }

  private get locationCategorySelector(): JSX.Element {
    const { location, operatorValues, equipment } = Localization.translator

    return (
      <>
        <MapPinIcon className="tw:size-4 tw:text-muted-foreground" />
        {`${location} ${operatorValues.and} ${equipment}`}
      </>
    )
  }

  private get companyCategorySelector(): JSX.Element {
    return (
      <>
        <Building2Icon className="tw:size-4 tw:text-muted-foreground" />
        {Localization.translator.workflowCompany}
      </>
    )
  }

  private handleAddUserToRule = (rule: OperationRule, userId: string) => {
    this.store.hideUsersDirectory()
    this.props.onChangeUserForRule(rule, userId, true)
  }

  private handleRemoveUserFromRule = (rule: OperationRule, userId: string) => {
    this.store.hideUsersDirectory()
    this.props.onChangeUserForRule(rule, userId)
  }

  private handleRenameRule = (name: string) => {
    this.props.onRenameRule(this.props.bicRule, name)
  }

  private handleNotLocationOperatorChange = (operator: IsNotOperator) => {
    this.store.changeNotOperator(operator)
  }

  private handleNotCompanyOperatorChange = (operator: IsNotOperator) => {
    this.store.changeNotOperator(operator, true)
  }

  private handleDiffCommitForRule = (
    rule: OperationRule,
    addedUserIds: string[],
    removedUserIds: string[],
    addedFieldIds: string[],
    removedFieldIds: string[],
    addedCompanyIds: string[],
    removedCompanyIds: string[],
  ) => {
    this.props.onDiffCommitForRule?.(
      rule,
      addedUserIds,
      removedUserIds,
      addedFieldIds,
      removedFieldIds,
      addedCompanyIds,
      removedCompanyIds,
    )
  }

  private handleFieldToggle = (fieldId: string, isSelected: boolean) => {
    const { onFieldToggleForRule } = this.props
    const { selectedBicRule } = this.store

    if (onFieldToggleForRule) {
      onFieldToggleForRule(selectedBicRule, fieldId, isSelected)
    }
  }

  private handleFieldsToggle = (fieldIds: string[], isSelected: boolean) => {
    const { onFieldsToggleForRule } = this.props
    const { selectedBicRule } = this.store

    if (onFieldsToggleForRule) {
      onFieldsToggleForRule(selectedBicRule, fieldIds, isSelected)
    }
  }

  private getAssignedFields(operationRule: OperationRule): IAssignedField[] {
    const { allWorkflowSteps } = this.props
    if (!allWorkflowSteps?.length) {
      return []
    }

    const assignedFieldIds = operationRule.assignedFieldIds
    if (!assignedFieldIds.length) {
      return []
    }

    const assignedFields: IAssignedField[] = []
    let stepNumber = 0

    allWorkflowSteps.forEach(step => {
      const userFields = step.fields?.filter(field =>
        userPermitFieldTypes.includes(field.type),
      )
      if (!userFields?.length) {
        return
      }
      stepNumber++

      userFields.forEach((field, fieldIdx) => {
        if (assignedFieldIds.includes(field.id)) {
          assignedFields.push({
            id: field.id,
            type: field.type,
            caption: field.caption || getWorkflowFieldTypeLabel(field.type),
            stepNumber,
            fieldIndex: fieldIdx + 1,
          })
        }
      })
    })

    return assignedFields
  }

  private handleRemoveCompanyFromRule = (
    rule: OperationRule,
    companyId: string,
  ) => {
    this.props.onCompanyDiffCommitForRule?.(rule, [], [companyId])
  }

  private handleRemoveFieldFromRule = (
    rule: OperationRule,
    fieldId: string,
  ) => {
    const { onFieldToggleForRule } = this.props
    if (onFieldToggleForRule) {
      onFieldToggleForRule(rule, fieldId, false)
    }
  }
}
